import { flushWorkflowDrafts, hasPendingWorkflowDrafts } from "./shared/navigation-guard";
import {
  Fragment,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode
} from "react";
import {
  ApiError,
  api,
  type CurrentUser,
  type Deployment,
  type Feature,
  type PlatformPermission
} from "./api";
import { I18nProvider, useI18n, type Locale } from "../i18n";
import {
  developmentIssueIdFromLocation,
  improvementCandidateIdFromLocation,
  notificationDeliveryIdFromLocation,
  normalizedPathname,
  pageFromLocation,
  pagePaths,
  releaseRoutePath,
  releaseRouteTargetFromLocation,
  skillGuidePathFromLocation,
  skillGuidePaths,
  type Page,
  type ReleaseRouteTarget
} from "./routing";
import { hasPermission } from "./shared/authorization";
import {
  completeLogin,
  type AuthState,
  type LoginResponse
} from "./shared/authentication";
import { localizedError } from "./shared/errors";
import { formatDate } from "./shared/format";
import { queryValue } from "./shared/query";
import { releaseNavigationHistoryState } from "./shared/release-queue-history";
import {
  EmptyState,
  PageLoadBoundary,
  PageLoading,
  Status
} from "./shared/ui";

const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const SkillPackPage = lazy(() => import("./pages/SkillPackPage"));
const ReleasesPage = lazy(() => import("./pages/ReleasesPage"));
const ComsPage = lazy(() => import("./pages/ComsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const AuditPage = lazy(() => import("./pages/AuditPage"));
const DevelopmentIssuesPage = lazy(
  () => import("./pages/DevelopmentIssuesPage")
);
const ImprovementCandidatesPage = lazy(
  () => import("./pages/ImprovementCandidatesPage")
);
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));

function LoginPage({
  onAuthenticated
}: {
  onAuthenticated: (auth: AuthState) => void;
}) {
  const { t, has } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function login(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const returnTo = queryValue("return_to");
      const result = await api<LoginResponse>(
        `/api/v1/auth/login${
          returnTo
            ? `?return_to=${encodeURIComponent(returnTo)}`
            : ""
        }`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email, password })
        }
      );
      setPassword("");
      completeLogin(result, {
        onAuthenticated,
        replaceLocation: (target) => window.location.replace(target)
      });
    } catch (reason) {
      setError(localizedError(reason, t, has, "auth.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <form className="login-card" onSubmit={login}>
        <span className="brand-mark">C</span>
        <span className="eyebrow">COMS FEATURE PLATFORM</span>
        <h1>{t("auth.signIn")}</h1>
        <p>{t("auth.subtitle")}</p>
        {error && <div className="notice notice-error">{error}</div>}
        <label htmlFor="login-email">{t("auth.email")}</label>
        <input
          id="login-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <label htmlFor="login-password">{t("auth.password")}</label>
        <input
          id="login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button className="button button-primary" disabled={busy}>
          {busy ? t("auth.signingIn") : t("auth.signIn")}
        </button>
      </form>
    </main>
  );
}

function HomePage({
  user,
  deployments,
  features,
  onOpenSkills
}: {
  user: CurrentUser;
  deployments: Deployment[];
  features: Feature[];
  onOpenSkills: () => void;
}) {
  const { locale, t } = useI18n();
  const canUpload = hasPermission(user, "feature.upload");
  const recent = deployments.slice(0, 3);
  return (
    <>
      <section className="hero">
        <div>
          <span className="eyebrow">{t("home.eyebrow")}</span>
          <h1>{t("home.title")}</h1>
          <p>{t("home.description")}</p>
          {hasPermission(user, "feature.use") && (
            <a
              className="button button-primary"
              href="/skills"
              onClick={(event) => {
                event.preventDefault();
                onOpenSkills();
              }}
            >
              {t("home.skillGuide")}
            </a>
          )}
        </div>
        <div className="flow-card" aria-label={t("home.workflowLabel")}>
          <div>
            <strong>01</strong>
            <span>{t("home.step1")}</span>
          </div>
          <div>
            <strong>02</strong>
            <span>{t("home.step2")}</span>
          </div>
          <div>
            <strong>03</strong>
            <span>{t("home.step3")}</span>
          </div>
        </div>
      </section>
      <section className="metric-grid">
        <article className="metric">
          <span>{t("home.availableFeatures")}</span>
          <strong>
            {
              features.filter((item) => item.can_use).length
            }
          </strong>
        </article>
        <article className="metric">
          <span>{t("home.accessRole")}</span>
          <strong>{user.roles.join(", ")}</strong>
        </article>
        <article className="metric">
          <span>{t("home.publicPath")}</span>
          <strong>/f/feature-key/</strong>
        </article>
      </section>
      {canUpload && (
        <section className="section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">{t("home.recent")}</span>
              <h2>{t("home.latestDeployments")}</h2>
            </div>
          </div>
          {recent.length === 0 ? (
            <EmptyState>{t("home.noDeployments")}</EmptyState>
          ) : (
            <div className="card-grid">
              {recent.map((deployment) => (
                <article className="card" key={deployment.id}>
                  <div className="card-top">
                    <span className="mono">{deployment.feature_key}</span>
                    <Status value={deployment.status} />
                  </div>
                  <h3>{t("common.version", { version: deployment.version })}</h3>
                  <p>
                    {deployment.error_message ??
                      t("home.deploymentDefault")}
                  </p>
                  <span className="muted">
                    {t("home.started", {
                      date: formatDate(
                        deployment.created_at,
                        locale,
                        t("common.notAvailable")
                      )
                    })}
                  </span>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </>
  );
}


function PlatformApp() {
  const { locale, setLocale, applyPreferredLocale, t, has } = useI18n();
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [page, setPage] = useState<Page>(pageFromLocation);
  const [releaseRouteTarget, setReleaseRouteTarget] =
    useState<ReleaseRouteTarget | null>(releaseRouteTargetFromLocation);
  const [developmentIssueId, setDevelopmentIssueId] = useState<string | null>(
    developmentIssueIdFromLocation
  );
  const [notificationDeliveryId, setNotificationDeliveryId] = useState<
    string | null
  >(notificationDeliveryIdFromLocation);
  const [skillGuidePath, setSkillGuidePath] = useState(
    skillGuidePathFromLocation
  );
  const [features, setFeatures] = useState<Feature[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    api<{ user: CurrentUser; csrf_token: string }>("/api/v1/auth/me")
      .then((result) => {
        applyPreferredLocale(result.user.preferred_locale);
        setAuth({ user: result.user, csrfToken: result.csrf_token });
      })
      .catch((reason) => {
        if (!(reason instanceof ApiError) || reason.status !== 401) {
          setAuthError(localizedError(reason, t, has, "auth.verifyFailed"));
        }
      })
      .finally(() => setAuthLoading(false));
  }, [applyPreferredLocale, has, t]);

  const changeLocale = useCallback(
    (next: Locale) => {
      setLocale(next);
      if (!auth) return;
      void api<{ preferred_locale: Locale }>(
        "/api/v1/auth/preferences",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ preferred_locale: next })
        },
        { csrfToken: auth.csrfToken }
      )
        .then(() => {
          setAuth((current) =>
            current
              ? {
                  ...current,
                  user: { ...current.user, preferred_locale: next }
                }
              : current
          );
        })
        .catch((reason) => {
          setLoadError(
            localizedError(reason, t, has, "preferences.saveFailed")
          );
        });
    },
    [auth, has, setLocale, t]
  );

  const refresh = useCallback(async () => {
    if (!auth) return;
    try {
      const result = await api<{
        items: Feature[];
        deployments: Deployment[];
      }>("/api/v1/features?view=LIVE&limit=100");
      setFeatures(result.items);
      setDeployments(result.deployments);
      setLoadError("");
    } catch (reason) {
      setLoadError(localizedError(reason, t, has, "features.loadFailed"));
    }
  }, [auth, has, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const navigation = useMemo(() => {
    if (!auth) return [];
    const items: Array<{ id: Page; label: string }> = [
      { id: "home", label: t("nav.home") },
      { id: "features", label: t("nav.features") },
      { id: "skill-pack", label: t("nav.skills") }
    ];
    if (hasPermission(auth.user, "feature.upload")) {
      items.push({ id: "coms", label: t("nav.coms") });
    }
    if (
      ["feature.upload", "feature.review", "feature.publish"].some(
        (permission) =>
          hasPermission(auth.user, permission as PlatformPermission)
      )
    ) {
      items.push({ id: "releases", label: t("nav.releases") });
    }
    if (
      hasPermission(auth.user, "support.issue.read_own") ||
      hasPermission(auth.user, "support.issue.read_all")
    ) {
      items.push({ id: "development-issues", label: t("nav.developmentIssues") });
    }
    if (hasPermission(auth.user, "support.issue.read_all")) {
      items.push({ id: "improvement-candidates", label: t("nav.improvementCandidates") });
    }
    if (hasPermission(auth.user, "platform.notification.read")) {
      items.push({ id: "notifications", label: t("nav.notifications") });
    }
    if (hasPermission(auth.user, "platform.user.read")) {
      items.push({ id: "users", label: t("nav.users") });
    }
    if (hasPermission(auth.user, "platform.audit.read")) {
      items.push({ id: "audit", label: t("nav.audit") });
    }
    return items;
  }, [auth, t]);

  const effectivePage =
    !auth || navigation.some((item) => item.id === page) ? page : "home";

  const navigate = useCallback(
    async (next: Page, options: { replace?: boolean } = {}) => {
      if (hasPendingWorkflowDrafts() && !await flushWorkflowDrafts()) return;
      setPage(next);
      setReleaseRouteTarget(null);
      setDevelopmentIssueId(null);
      setNotificationDeliveryId(null);
      const url = new URL(window.location.href);
      url.pathname = pagePaths[next];
      url.hash = "";
      if (next === "skill-pack") setSkillGuidePath("/skills");
      const target = `${url.pathname}${url.search}${url.hash}`;
      const current =
        `${window.location.pathname}${window.location.search}` +
        window.location.hash;
      if (target === current) return;
      if (options.replace) window.history.replaceState(null, "", target);
      else window.history.pushState(null, "", target);
    },
    []
  );

  const navigateRelease = useCallback(
    (
      target: ReleaseRouteTarget | null,
      options: { replace?: boolean } = {}
    ) => {
      setPage("releases");
      setReleaseRouteTarget(target);
      setDevelopmentIssueId(null);
      setNotificationDeliveryId(null);
      const url = new URL(window.location.href);
      url.pathname = target ? releaseRoutePath(target) : pagePaths.releases;
      url.hash = "";
      const nextUrl = `${url.pathname}${url.search}`;
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      if (nextUrl === currentUrl) return;
      const state = releaseNavigationHistoryState(target !== null);
      if (options.replace) window.history.replaceState(state, "", nextUrl);
      else window.history.pushState(state, "", nextUrl);
    },
    []
  );

  const navigateDevelopmentIssue = useCallback(
    async (issueId: string | null, options: { replace?: boolean } = {}) => {
      if (hasPendingWorkflowDrafts() && !await flushWorkflowDrafts()) return;
      setPage("development-issues");
      setReleaseRouteTarget(null);
      setDevelopmentIssueId(issueId);
      setNotificationDeliveryId(null);
      const url = new URL(window.location.href);
      url.pathname = issueId
        ? `/development-issues/${issueId}`
        : pagePaths["development-issues"];
      url.hash = "";
      const target = `${url.pathname}${url.search}`;
      const current = `${window.location.pathname}${window.location.search}`;
      if (target === current) return;
      if (options.replace) window.history.replaceState(null, "", target);
      else window.history.pushState(null, "", target);
    },
    []
  );

  const navigateSkillGuide = useCallback(
    (path: string, options: { replace?: boolean } = {}) => {
      const normalized = normalizedPathname(path);
      const nextPath = skillGuidePaths.has(normalized) ? normalized : "/skills";
      setPage("skill-pack");
      setDevelopmentIssueId(null);
      setNotificationDeliveryId(null);
      setSkillGuidePath(nextPath);
      const url = new URL(window.location.href);
      url.pathname = nextPath;
      url.hash = "";
      const target = `${url.pathname}${url.search}`;
      const current = `${window.location.pathname}${window.location.search}`;
      if (target === current) return;
      if (options.replace) window.history.replaceState(null, "", target);
      else window.history.pushState(null, "", target);
    },
    []
  );

  const navigateNotification = useCallback(
    (deliveryId: string | null, options: { replace?: boolean } = {}) => {
      setPage("notifications");
      setReleaseRouteTarget(null);
      setDevelopmentIssueId(null);
      setNotificationDeliveryId(deliveryId);
      const url = new URL(window.location.href);
      url.pathname = deliveryId
        ? `/admin/notifications/${deliveryId}`
        : pagePaths.notifications;
      url.hash = "";
      const target = `${url.pathname}${url.search}`;
      const current = `${window.location.pathname}${window.location.search}`;
      if (target === current) return;
      if (options.replace) window.history.replaceState(null, "", target);
      else window.history.pushState(null, "", target);
    },
    []
  );

  useEffect(() => {
    const previousPath = window.location.pathname + window.location.search;
    const onPopState = async () => {
      if (hasPendingWorkflowDrafts() && !await flushWorkflowDrafts()) {
        window.history.pushState(null, "", previousPath);
        return;
      }
      setPage(pageFromLocation());
      setReleaseRouteTarget(releaseRouteTargetFromLocation());
      setDevelopmentIssueId(developmentIssueIdFromLocation());
      setNotificationDeliveryId(notificationDeliveryIdFromLocation());
      setSkillGuidePath(skillGuidePathFromLocation());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [page, developmentIssueId, releaseRouteTarget, notificationDeliveryId, skillGuidePath]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [developmentIssueId, notificationDeliveryId, page, skillGuidePath]);

  useEffect(() => {
    if (!auth) return;
    if (!navigation.some((navigationItem) => navigationItem.id === page)) {
      navigate("home", { replace: true });
      return;
    }
    if (page === "skill-pack") {
      const currentPath = normalizedPathname(window.location.pathname);
      if (!skillGuidePaths.has(currentPath)) {
        navigateSkillGuide("/skills", { replace: true });
      } else if (currentPath !== skillGuidePath) {
        setSkillGuidePath(currentPath);
      }
      return;
    }
    if (page === "releases" && releaseRouteTarget) {
      if (
        normalizedPathname(window.location.pathname) !==
        releaseRoutePath(releaseRouteTarget)
      ) {
        navigateRelease(releaseRouteTarget, { replace: true });
      }
      return;
    }
    if (page === "development-issues" && developmentIssueId) {
      const expected = `/development-issues/${developmentIssueId}`;
      if (normalizedPathname(window.location.pathname) !== expected) {
        navigateDevelopmentIssue(developmentIssueId, { replace: true });
      }
      return;
    }
    if (page === "improvement-candidates" && improvementCandidateIdFromLocation()) {
      return;
    }
    if (page === "notifications" && notificationDeliveryId) {
      const expected = `/admin/notifications/${notificationDeliveryId}`;
      if (normalizedPathname(window.location.pathname) !== expected) {
        navigateNotification(notificationDeliveryId, { replace: true });
      }
      return;
    }
    if (normalizedPathname(window.location.pathname) !== pagePaths[page]) {
      navigate(page, { replace: true });
    }
  }, [
    auth,
    navigate,
    navigateRelease,
    navigateDevelopmentIssue,
    navigateNotification,
    navigateSkillGuide,
    navigation,
    page,
    developmentIssueId,
    notificationDeliveryId,
    releaseRouteTarget,
    skillGuidePath
  ]);

  async function logout() {
    if (!auth) return;
    try {
      await api(
        "/api/v1/auth/logout",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: "{}"
        },
        { csrfToken: auth.csrfToken }
      );
    } finally {
      setAuth(null);
      setFeatures([]);
      setDeployments([]);
      navigate("home", { replace: true });
    }
  }

  if (authLoading) {
    return <div className="loading-state">{t("auth.checking")}</div>;
  }

  if (!auth) {
    return (
      <>
        {authError && (
          <div className="notice notice-error global-notice">{authError}</div>
        )}
        <LoginPage
          onAuthenticated={(next) => {
            applyPreferredLocale(next.user.preferred_locale);
            setAuth(next);
            setAuthError("");
          }}
        />
      </>
    );
  }

  let content: ReactNode;
  if (effectivePage === "features") {
    content = (
      <FeaturesPage
        user={auth.user}
        csrfToken={auth.csrfToken}
        refresh={refresh}
      />
    );
  } else if (effectivePage === "skill-pack") {
    content = (
      <SkillPackPage
        user={auth.user}
        path={skillGuidePath}
        onNavigate={navigateSkillGuide}
      />
    );
  } else if (effectivePage === "releases") {
    content = (
      <ReleasesPage
        key={JSON.stringify([auth.user.email, auth.user.permissions])}
        user={auth.user}
        csrfToken={auth.csrfToken}
        onPublished={refresh}
        routeTarget={releaseRouteTarget}
        onRouteTargetChange={navigateRelease}
      />
    );
  } else if (effectivePage === "coms") {
    content = <ComsPage />;
  } else if (effectivePage === "development-issues") {
    content = (
      <DevelopmentIssuesPage
        user={auth.user}
        csrfToken={auth.csrfToken}
        issueId={developmentIssueId}
        onOpenIssue={navigateDevelopmentIssue}
      />
    );
  } else if (effectivePage === "improvement-candidates") {
    content = (
      <ImprovementCandidatesPage
        user={auth.user}
        csrfToken={auth.csrfToken}
      />
    );
  } else if (effectivePage === "notifications") {
    content = (
      <NotificationsPage
        csrfToken={auth.csrfToken}
        deliveryId={notificationDeliveryId}
        onOpenDelivery={navigateNotification}
      />
    );
  } else if (effectivePage === "users") {
    content = (
      <UsersPage
        currentUser={auth.user}
        csrfToken={auth.csrfToken}
      />
    );
  } else if (effectivePage === "audit") {
    content = <AuditPage />;
  } else {
    content = (
      <HomePage
        user={auth.user}
        deployments={deployments}
        features={features}
        onOpenSkills={() => navigate("skill-pack")}
      />
    );
  }

  return (
    <div className="app-shell">
      <header>
        <button
          className="brand"
          onClick={() => navigate("home")}
          aria-label={t("brand.home")}
        >
          <span className="brand-mark">C</span>
          <span>
            <strong>COMS</strong>
            <small>{t("brand.subtitle")}</small>
          </span>
        </button>
        <nav>
          {navigation.map((item) => (
            <button
              className={page === item.id ? "active" : ""}
              key={item.id}
              onClick={() => navigate(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="session-control">
          <select
            aria-label={t("locale.selector")}
            value={locale}
            onChange={(event) => changeLocale(event.target.value as Locale)}
          >
            <option value="zh-CN">{t("locale.zh-CN")}</option>
            <option value="en">{t("locale.en")}</option>
          </select>
          <span>{auth.user.display_name ?? auth.user.email}</span>
          <button type="button" onClick={() => void logout()}>
            {t("auth.signOut")}
          </button>
        </div>
      </header>
      <main>
        {loadError && (
          <div className="notice notice-error global-notice">{loadError}</div>
        )}
        <PageLoadBoundary
          resetKey={releaseRouteTarget?.releaseId ?? ""}
          key={`${effectivePage}:${developmentIssueId ?? ""}:${notificationDeliveryId ?? ""}:${skillGuidePath}`}
        >
          <Suspense fallback={<PageLoading />}>{content}</Suspense>
        </PageLoadBoundary>
      </main>
      <footer>
        <span>{t("footer.identity")}</span>
        <span>{t("footer.runtime")}</span>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <PlatformApp />
    </I18nProvider>
  );
}
